import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  BarChart3, TrendingUp, Users, Coins, Truck, Wrench, 
  ClipboardCheck, Home, Trophy, Activity 
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const APP_INFO = {
  ridero: { name: 'Ridero', icon: Truck, color: 'bg-blue-500', textColor: 'text-blue-500' },
  pti_pector: { name: 'PTI Pector', icon: ClipboardCheck, color: 'bg-green-500', textColor: 'text-green-500' },
  mechanic_kiosk: { name: 'Mechanic Kiosk', icon: Wrench, color: 'bg-orange-500', textColor: 'text-orange-500' },
  hub: { name: 'Leader Hub', icon: Home, color: 'bg-purple-500', textColor: 'text-purple-500' },
  referral: { name: 'Referrals', icon: Users, color: 'bg-pink-500', textColor: 'text-pink-500' }
};

export default function AppStatsPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [overview, setOverview] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [appDetails, setAppDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (selectedApp) {
      fetchAppDetails(selectedApp);
    }
  }, [selectedApp]);

  const fetchStats = async () => {
    try {
      const [statsRes, overviewRes] = await Promise.all([
        axios.get(`${API}/api/stats/apps`, { withCredentials: true }),
        axios.get(`${API}/api/stats/overview`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setOverview(overviewRes.data);
    } catch (error) {
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchAppDetails = async (appId) => {
    try {
      const res = await axios.get(`${API}/api/stats/apps/${appId}`, { withCredentials: true });
      setAppDetails(res.data);
    } catch (error) {
      toast.error('Failed to load app details');
    }
  };

  if (user?.role_id !== 'master_admin' && user?.role_id !== 'admin') {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Admin access required</p>
        </div>
      </DashboardLayout>
    );
  }

  const getAppInfo = (appId) => APP_INFO[appId] || { 
    name: appId, 
    icon: Activity, 
    color: 'bg-gray-500',
    textColor: 'text-gray-500'
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <BarChart3 className="w-8 h-8" />
            App Statistics
          </h1>
          <p className="text-muted-foreground mt-1">
            Track points collection across all connected apps
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Users</p>
                      <p className="text-3xl font-bold">{overview?.users?.total || 0}</p>
                    </div>
                    <Users className="w-10 h-10 text-blue-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Points Awarded</p>
                      <p className="text-3xl font-bold">{overview?.points?.total_awarded?.toLocaleString() || 0}</p>
                    </div>
                    <Coins className="w-10 h-10 text-yellow-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Points in Circulation</p>
                      <p className="text-3xl font-bold">{overview?.points?.total_balance?.toLocaleString() || 0}</p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-green-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Last 7 Days</p>
                      <p className="text-3xl font-bold">+{overview?.last_7_days?.points_awarded?.toLocaleString() || 0}</p>
                      <p className="text-xs text-muted-foreground">{overview?.last_7_days?.transactions || 0} transactions</p>
                    </div>
                    <Activity className="w-10 h-10 text-purple-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* App Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {stats?.apps?.map(app => {
                const info = getAppInfo(app.app_id);
                const Icon = info.icon;
                return (
                  <Card 
                    key={app.app_id} 
                    className={`cursor-pointer transition-all hover:shadow-lg ${selectedApp === app.app_id ? 'ring-2 ring-primary' : ''}`}
                    onClick={() => setSelectedApp(app.app_id)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${info.color}`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <CardTitle className="text-lg">{info.name}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Total Points</span>
                          <span className={`text-xl font-bold ${info.textColor}`}>
                            {app.total_points?.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Transactions</span>
                          <span className="font-medium">{app.total_transactions}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-muted-foreground">Active Users</span>
                          <span className="font-medium">{app.unique_users}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* App Details */}
            {selectedApp && appDetails && (
              <Card className="mt-6">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    {(() => {
                      const info = getAppInfo(selectedApp);
                      const Icon = info.icon;
                      return (
                        <>
                          <div className={`p-2 rounded-lg ${info.color}`}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <CardTitle>{info.name} Details</CardTitle>
                            <CardDescription>
                              {appDetails.summary?.unique_users} users • {appDetails.summary?.total_transactions} transactions
                            </CardDescription>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="events">
                    <TabsList>
                      <TabsTrigger value="events">Event Breakdown</TabsTrigger>
                      <TabsTrigger value="top">Top Earners</TabsTrigger>
                    </TabsList>

                    <TabsContent value="events" className="mt-4">
                      <div className="space-y-2">
                        {appDetails.event_breakdown?.map(event => (
                          <div 
                            key={event.event_type}
                            className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                          >
                            <div>
                              <div className="font-medium">{event.event_type?.replace(/_/g, ' ')}</div>
                              <div className="text-sm text-muted-foreground">
                                {event.count} times • avg {event.avg_points} pts
                              </div>
                            </div>
                            <Badge variant="secondary" className="text-lg px-3">
                              {event.total_points?.toLocaleString()} pts
                            </Badge>
                          </div>
                        ))}
                        {appDetails.event_breakdown?.length === 0 && (
                          <p className="text-center text-muted-foreground py-8">No events recorded yet</p>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="top" className="mt-4">
                      <div className="space-y-2">
                        {appDetails.top_earners?.map((earner, index) => (
                          <div 
                            key={earner.user_id}
                            className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                index === 0 ? 'bg-yellow-500' : 
                                index === 1 ? 'bg-gray-400' : 
                                index === 2 ? 'bg-orange-400' : 'bg-muted'
                              }`}>
                                {index < 3 ? (
                                  <Trophy className="w-4 h-4 text-white" />
                                ) : (
                                  <span className="text-sm font-bold">{index + 1}</span>
                                )}
                              </div>
                              <div>
                                <div className="font-medium">{earner.name || 'Unknown'}</div>
                                <div className="text-sm text-muted-foreground">{earner.email}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-lg">{earner.total_points?.toLocaleString()} pts</div>
                              <div className="text-sm text-muted-foreground">{earner.transactions} events</div>
                            </div>
                          </div>
                        ))}
                        {appDetails.top_earners?.length === 0 && (
                          <p className="text-center text-muted-foreground py-8">No earners yet</p>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
